import {
  CalendarDetailsVisibility,
  ExternalConnection,
  ID,
  SyncMode,
  Task,
} from '@griply/types';

import {
  getTaskLinkedItemIds,
  LinkedItemGoal,
} from './get-task-linked-item-ids';

export type TaskCalendarSyncTarget = {
  connectionId: ID;
  calendarId: ID;
  linkedItemId: ID;
  visibility: CalendarDetailsVisibility;
};

export type GetTaskCalendarSyncTargetsOptions = {
  task: Pick<Task, 'id' | 'goalId' | 'lifeAreaId'>;
  connections: ExternalConnection[];
  goalsById: Map<ID, LinkedItemGoal>;
};

export const getTaskCalendarSyncTargets = ({
  task,
  connections,
  goalsById,
}: GetTaskCalendarSyncTargetsOptions): TaskCalendarSyncTarget[] => {
  const linkedItemIds = getTaskLinkedItemIds(task, goalsById);
  if (!linkedItemIds.size) {
    return [];
  }

  return connections.flatMap((connection) => {
    const calendarSettings = connection.settings?.calendarSettings;
    if (!calendarSettings) {
      return [];
    }

    const override = connection.settings?.taskOverrides?.[task.id];

    return [...linkedItemIds].flatMap((linkedItemId) => {
      const setting = calendarSettings[linkedItemId];
      if (!setting) {
        return [];
      }

      const synced = override?.synced ?? setting.mode === SyncMode.Automatic;
      if (!synced) {
        return [];
      }

      const visibility =
        override?.detailsVisibility ??
        setting.detailsVisibility ??
        CalendarDetailsVisibility.Visible;

      return [
        {
          connectionId: connection.id,
          calendarId: setting.calendarId,
          linkedItemId,
          visibility,
        },
      ];
    });
  });
};
